/**
 * Usuários — quem tem acesso, e desde quando não aparece.
 *
 * Esta tela é a mitigação combinada quando decidimos ter login próprio em vez
 * de reusar o do sistema da Nex. O argumento mais forte contra o login próprio
 * era: com dois sistemas, o desligamento vira dois lugares para lembrar, e o
 * segundo é o que se esquece.
 *
 * A resposta não é automação — é **visibilidade**. Uma lista de cinco linhas
 * com o último acesso torna a auditoria uma olhada de dez segundos, e um
 * esquecimento deixa de ser invisível. É o invisível que faz o problema durar.
 *
 * Por isso o "nunca entrou" e o "sem entrar há N dias" são destacados: são o
 * sintoma de conta que sobrou.
 */
import { Api, type UsuarioLogado } from "../api.js";
import { el, horario, limpar } from "../dom.js";
import { avisar, type Tela } from "../tela.js";

/** Acima disto, a conta parece esquecida. */
const DIAS_PARA_ESTRANHAR = 60;

function diasDesde(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((Date.now() - d) / 86_400_000);
}

export function telaUsuarios(): Tela {
  return {
    titulo: "Usuários",
    async montar(raiz) {
      const lista = el("div", { class: "cartoes" });

      const recarregar = async (): Promise<void> => {
        limpar(lista);
        const { users } = await Api.usuarios();
        for (const u of users) lista.appendChild(cartao(u, raiz, recarregar));
      };

      raiz.appendChild(
        el(
          "p",
          { class: "sub" },
          "Quem pode entrar. Desligar tira o acesso na hora — a sessão aberta " +
            "cai na requisição seguinte, sem esperar vencer."
        )
      );
      raiz.appendChild(formularioNovo(raiz, recarregar));
      raiz.appendChild(el("h3", {}, "Com acesso"));
      raiz.appendChild(lista);

      await recarregar();
    }
  };
}

function formularioNovo(
  raiz: HTMLElement,
  recarregar: () => Promise<void>
): HTMLElement {
  const nome = el("input", { placeholder: "nome" }) as HTMLInputElement;
  const email = el("input", {
    type: "email",
    placeholder: "e-mail"
  }) as HTMLInputElement;
  const senha = el("input", {
    type: "password",
    placeholder: "senha provisória (mín. 10)",
    autocomplete: "new-password"
  }) as HTMLInputElement;

  const estado = el("p", { class: "sub" });
  const botao = el("button", {}, "Criar") as HTMLButtonElement;

  botao.addEventListener("click", () => {
    botao.disabled = true;
    estado.className = "sub";
    estado.textContent = "criando…";

    void Api.criarUsuario({
      name: nome.value,
      email: email.value,
      senha: senha.value
    })
      .then(async (r) => {
        estado.className = "saida ok";
        estado.textContent = `${r.user.name} criado. Passe a senha por um canal seguro e peça para trocar.`;
        nome.value = "";
        email.value = "";
        senha.value = "";
        await recarregar();
      })
      .catch((err: unknown) => {
        estado.textContent = "";
        avisar(raiz, err, "Não deu para criar");
      })
      .finally(() => {
        botao.disabled = false;
      });
  });

  return el(
    "div",
    { class: "caixa" },
    el("h3", {}, "Dar acesso a alguém"),
    el(
      "p",
      { class: "sub" },
      "A senha é provisória e definida por você — não há envio de e-mail neste " +
        "sistema, de propósito. Mande por um canal seguro e troque depois."
    ),
    el("div", { class: "barra" }, nome, email, senha, botao),
    estado
  );
}

function cartao(
  u: UsuarioLogado,
  raiz: HTMLElement,
  recarregar: () => Promise<void>
): HTMLElement {
  const dias = diasDesde(u.lastLoginAt);
  const sumido = u.isActive && (dias === null || dias > DIAS_PARA_ESTRANHAR);

  const acesso =
    u.lastLoginAt === null
      ? el("span", { class: "erro-linha" }, "nunca entrou")
      : el(
          "span",
          { class: sumido ? "erro-linha" : "sub" },
          `último acesso ${horario(u.lastLoginAt)}` +
            (dias !== null && dias > 0 ? ` · há ${dias} dia(s)` : "")
        );

  const acoes = el("div", { class: "acoes" });

  const alternar = el(
    "button",
    { class: u.isActive ? "perigo" : "secundario" },
    u.isActive ? "Desligar" : "Religar"
  ) as HTMLButtonElement;

  alternar.addEventListener("click", () => {
    if (
      u.isActive &&
      !confirm(
        `Tirar o acesso de ${u.name}?\n\n` +
          `A sessão aberta cai na requisição seguinte. Dá para religar depois, ` +
          `e o histórico do que a pessoa aprovou é preservado.`
      )
    )
      return;

    alternar.disabled = true;
    void Api.definirAtivo(u.id, !u.isActive)
      .then(() => recarregar())
      .catch((err: unknown) => {
        alternar.disabled = false;
        // O 409 de "não desligue a si mesmo" chega com texto explicativo.
        avisar(raiz, err, "Não deu para mudar");
      });
  });
  acoes.appendChild(alternar);

  const trocar = el(
    "button",
    { class: "secundario" },
    "Trocar senha"
  ) as HTMLButtonElement;
  trocar.addEventListener("click", () => {
    const nova = prompt(
      `Nova senha para ${u.name} (mínimo 10 caracteres).\n\n` +
        `Trocar derruba todas as sessões dessa pessoa.`
    );
    if (nova === null) return;
    trocar.disabled = true;
    void Api.trocarSenha(u.id, nova)
      .then(() => {
        trocar.disabled = false;
        alert("Senha trocada. As sessões anteriores foram encerradas.");
      })
      .catch((err: unknown) => {
        trocar.disabled = false;
        avisar(raiz, err, "Não deu para trocar a senha");
      });
  });
  acoes.appendChild(trocar);

  return el(
    "article",
    { class: "cartao" },
    el(
      "header",
      {},
      el("h3", {}, u.name),
      el(
        "span",
        { class: `pilula ${u.isActive ? "ok" : "off"}` },
        u.isActive ? "ativo" : "desligado"
      )
    ),
    el(
      "dl",
      {},
      el("dt", {}, "e-mail"),
      el("dd", { class: "mono" }, u.email),
      el("dt", {}, "acesso"),
      el("dd", {}, acesso),
      el("dt", {}, "criado"),
      el("dd", { class: "sub" }, horario(u.createdAt))
    ),
    acoes
  );
}
